// Plain-English reporting for "↻ Re-sync from Looker".
//
// A re-sync that finds nothing used to be a dead end ("no changes to pull") — the
// admin had no way to tell a Looker dashboard that genuinely didn't change from a
// Looker edit that never saved, a dashboard pointing at the wrong source, or tiles
// Looker refused to hand over. The server now returns what Looker itself says
// about the source (see server/resync.js → lookerMeta); these helpers turn that
// into the lines shown in the re-sync dialogs.
//
// Pure + dependency-free so it's unit-testable outside React.

// Format a Looker timestamp for a human, falling back to the raw string.
export function whenText(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

// What Looker says about the source dashboard: identity, size, when it last
// changed (and by whom), any LookML caveat, and a link to open it in Looker.
export function lookerSourceReport(looker, fallbackId) {
  const id = looker?.id || fallbackId || '?';
  if (!looker) return `Looker source: id ${id}`;
  const when = whenText(looker.updatedAt);
  return [
    `Looker source: “${looker.title || '?'}” (id ${id}${looker.folder ? ` · Looker folder “${looker.folder}”` : ''})`,
    `• ${looker.elements || 0} tile${looker.elements === 1 ? '' : 's'} in Looker`,
    when ? `• Last changed in Looker: ${when}${looker.updatedBy ? ` by ${looker.updatedBy}` : ''}` : '',
    looker.lookml ? '• This is a LookML-defined dashboard — an edit only shows up here once the LookML is DEPLOYED to production in Looker (a dev-branch change is invisible to Pulse).' : '',
    arrangementNote(looker.arrangement),
    looker.url ? `• Open it in Looker: ${looker.url}` : '',
  ].filter(Boolean).join('\n');
}

// Did Looker tell us where its tiles sit? When it doesn't, the only order Pulse
// can produce is Looker's internal element order — which reads as random and is
// indistinguishable from a clean import unless we say so. Restoring the
// arrangement can't help in that case, so the note points at Looker instead.
export function arrangementNote(a) {
  if (!a || !a.total) return '';
  if (a.from === 'stacked') return `• ⚠ Looker did not return a usable arrangement for this dashboard (${a.layouts ? `${a.layouts} layout${a.layouts === 1 ? '' : 's'} sent, none naming these tiles` : 'no layout sent'}), so its tiles could only be stacked in Looker's own element order. Open the dashboard in Looker and re-save its layout, then re-sync — or arrange the tiles here in the editor.`;
  // Always say which layout was used: when the order is still wrong, this is the
  // line that tells "Looker's saved layout is wrong" from "Pulse misread it".
  const which = a.layout ? `Looker's ${a.layout.type ? `“${a.layout.type}” ` : ''}layout${a.layout.id ? ` (id ${a.layout.id}${a.layout.active ? '' : ', not the active one'})` : ''}` : "Looker's layout";
  const matched = a.byTitle ? ` (${a.byTitle} matched by tile title)` : '';
  // A tabbed Looker dashboard: one layout per tab, laid out here one after another.
  if (Array.isArray(a.tabs) && a.tabs.length > 1) {
    const names = a.tabs.map((t, i) => `“${t.label || `Tab ${i + 1}`}” (${t.placed})`).join(' → ');
    return `• Tile positions: this Looker dashboard has ${a.tabs.length} tabs — Pulse shows them one after another, each in its own Looker order: ${names}. ${a.placed} of ${a.total} tiles placed${matched}.`;
  }
  if (a.placed < a.total) return `• Tile positions: ${which} placed ${a.placed} of ${a.total} tiles${matched}; the rest have no position in Looker and sit at the bottom.`;
  return `• Tile positions: ${which} placed all ${a.total} tiles${matched}.`;
}

// Re-sync found nothing to move: Pulse already has every tile exactly where
// Looker's layout puts it. Without this the dialog read as "re-sync does
// nothing" — say plainly that the order IS Looker's, and how to change it here.
export function arrangementMatches(summary) {
  if (!summary || summary.layoutDrift) return '';
  const a = summary.arrangement;
  if (a && !(a.placed || 0)) return '';
  return "Tile order: every tile already sits where Looker's layout puts it, so there is nothing to restore. If the order still looks wrong, it's wrong in Looker's saved layout too — fix it there and re-sync, or arrange it here: drag tiles by their ✥ handle on a computer, or use ↑/↓ in the editor on a phone, then Save.";
}

// Does Looker's arrangement differ from this dashboard's? Offered, never applied
// on its own: a dashboard arranged deliberately in Pulse SHOULD read as drifted.
export function layoutDriftNote(summary, looker) {
  const n = summary?.layoutDrift || 0;
  if (!n) return '';
  if (looker?.arrangement?.from === 'stacked') return '';
  return `${n} tile${n === 1 ? '' : 's'} sit somewhere other than where Looker has them.\nSay yes below to ALSO restore Looker's tile arrangement — use this when the import itself came out scrambled. Tiles you added in Pulse, and your carousels/sections, keep their own rows underneath. Say no to keep the arrangement exactly as it is (the normal behaviour).`;
}

// Tiles Looker could not hand over a runnable query for. Their existing Pulse
// setup is KEPT (never blanked), but the admin needs to know the refresh skipped
// them — otherwise a broken Look reads as "everything is fine".
export function resyncWarnings(summary, looker) {
  const names = [
    ...((summary?.unreadable_) || []),
    ...((looker?.unresolved) || []).filter((n) => !((summary?.unreadable_) || []).includes(n)),
  ];
  if (!names.length) return '';
  return `⚠ Looker couldn't read ${names.length} tile${names.length === 1 ? '' : 's'} (${names.slice(0, 6).join(', ')}${names.length > 6 ? '…' : ''}).\nPulse kept what those tiles already have instead of blanking them. Usually the Look behind the tile was deleted, renamed or isn't shared with the Pulse Looker user — fix it in Looker, then re-sync again.`;
}

// ─── Tile ORDER ──────────────────────────────────────────────────────────────
// A re-sync refreshes content but deliberately leaves the arrangement alone, so
// a dashboard that imported in the wrong order could never be repaired from the
// re-sync ("I re-sync from Looker but it doesn't do anything"). It was then
// offered as a separate yes/no, which was easy to miss; since tickets #27/#126
// the re-sync restores Looker's arrangement whenever these report something to
// restore (the confirmation names how many tiles move). The question text is
// kept as the "is there anything to restore?" signal.

// The question to put to the admin, or '' when there is nothing to ask.
export function arrangementOffer(summary) {
  const drift = summary?.layoutDrift || 0;
  if (!drift) return '';
  if (!lookerPlacedTiles(summary)) return '';
  return [
    `Also restore Looker's tile arrangement?`,
    `${drift} tile${drift === 1 ? ' is' : 's are'} in a different place here than in Looker.`,
    'OK = put them back in Looker\'s order (tiles you added in Pulse, carousels and sections move below, nothing is deleted). Cancel = refresh the content only and keep this dashboard\'s own arrangement.',
  ].join('\n\n');
}

// Did Looker actually send an arrangement? When it didn't, "restore Looker's
// order" has nothing to restore — say so instead of offering a no-op.
export function lookerPlacedTiles(summary) {
  const a = summary?.arrangement;
  if (!a) return true; // older server / unknown — don't suppress the offer
  return (a.placed || 0) > 0;
}

// What the re-sync did (or couldn't do) to the order.
export function arrangementReport(summary) {
  if (!summary) return '';
  const a = summary.arrangement;
  if (a && !(a.placed || 0) && (a.total || 0) > 0) {
    return `⚠ Looker sent no tile arrangement for this dashboard, so Pulse can only fall back to the order Looker lists the tiles in (its internal id order, which reads as random). Open the dashboard in Looker, move a tile and save it — that writes a layout — then re-sync again.`;
  }
  if (!summary.relayout) return '';
  if (!summary.moved && !summary.pushed) return 'Tile arrangement already matched Looker — nothing moved.';
  return [
    `Tile arrangement restored from Looker: ${summary.moved} tile${summary.moved === 1 ? '' : 's'} moved`,
    summary.pushed ? ` (and ${summary.pushed} Pulse-added tile${summary.pushed === 1 ? '/carousel was' : 's/carousels were'} shifted below them)` : '',
    '.',
  ].join('');
}

// The same question for a whole folder. `totals.layoutDrift` counts tiles, not
// dashboards, so the copy names both.
export function folderArrangementOffer(totals, results) {
  const drift = totals?.layoutDrift || 0;
  if (!drift) return '';
  const dashboards = (results || []).filter((r) => (r?.layoutDrift || 0) > 0).length;
  return [
    `Also restore Looker's tile arrangement?`,
    `${drift} tile${drift === 1 ? '' : 's'} across ${dashboards || 1} dashboard${dashboards === 1 ? '' : 's'} sit somewhere else in Looker.`,
    'OK = put them back in Looker\'s order on every one of those dashboards (tiles added in Pulse, carousels and sections move below; nothing is deleted). Cancel = refresh the content only.',
  ].join('\n\n');
}

// Folder-wide re-sync: the same warnings, rolled up across every dashboard.
export function folderWarnings(results) {
  const names = [];
  for (const r of results || []) for (const n of resyncTileNames(r)) if (!names.includes(`${r.title}: ${n}`)) names.push(`${r.title}: ${n}`);
  if (!names.length) return '';
  return `⚠ Looker couldn't read ${names.length} tile${names.length === 1 ? '' : 's'} (${names.slice(0, 6).join(', ')}${names.length > 6 ? '…' : ''}).\nThose tiles kept what they already have. Check the Look behind each one in Looker, then re-sync again.`;
}

function resyncTileNames(r) {
  const own = (r?.unreadable_) || [];
  return [...own, ...(((r?.looker) || {}).unresolved || []).filter((n) => !own.includes(n))];
}

// "When did Looker last change each of these?" — the folder-level version of the
// question a fruitless re-sync raises. Most recently changed first, capped.
export function lookerFolderReport(results, limit = 6) {
  const rows = (results || [])
    .filter((r) => r?.looker)
    .map((r) => ({ title: r.title, at: r.looker.updatedAt || '', by: r.looker.updatedBy || '' }))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)))
    .slice(0, limit);
  if (!rows.length) return '';
  return ['Last changed in Looker:', ...rows.map((r) => `• “${r.title}” — ${whenText(r.at) || 'unknown'}${r.by ? ` by ${r.by}` : ''}`)].join('\n');
}
